import type { Landmark, CaptureSequence, CustomLandmarks } from '@/components/posture-analysis/types';
import {
  applyFrontalHipIliacBias,
  applyFrontalShoulderTowardHeadBias,
  pelvisPinsOverrideHip,
} from '@/components/posture-analysis/lib/pose-landmark-bias';
import type { AnalysisResult, AnalysisData } from './types';
import { getEvaluationRules } from '../evaluation-rules-load';

const evaluationRules = () => getEvaluationRules() as any;

const rad2deg = (r: number) => (r * 180) / Math.PI;
const distPx = (a: Landmark, b: Landmark) => Math.hypot(a.x - b.x, a.y - b.y);
const angleAt = (a: Landmark, b: Landmark, c: Landmark) => {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y }, cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y), mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
};
const vertFrom = (a: Landmark, b: Landmark) => rad2deg(Math.atan2(a.x - b.x, b.y - a.y));
const vertFromLeftCalcaneus = (a: Landmark, b: Landmark) => rad2deg(Math.atan2(b.x - a.x, b.y - a.y));

export const sev = (value: number | undefined | null, bands: [number, number, number]): number => {
  if (value === undefined || value === null || !isFinite(value)) return 0;
  const x = Math.abs(value);
  if (x < bands[0]) return 0;
  if (x < bands[1]) return 1;
  if (x < bands[2]) return 2;
  return 3;
};

const cmToIn = (cm: number) => cm / 2.54;
const fmtLenRaw = (cm: number, unit: 'cm' | 'inches', dec = 1) =>
  unit === 'inches' ? cmToIn(cm).toFixed(dec) : cm.toFixed(dec);

export const fmtLenWithUnit = (cm: number, unit: 'cm' | 'inches' = 'cm', dec = 1) =>
  isFinite(cm) ? `${fmtLenRaw(cm, unit, dec)} ${unit}` : '—';

export const fmtSignedDeg = (d: number, sideR = 'Right', sideL = 'Left', dec = 1) =>
  !isFinite(d) ? '—' : `${Math.abs(d).toFixed(dec)}° ${d >= 0 ? sideR : sideL}`;

const cloneLandmarks = (lm: Landmark[]): Landmark[] => lm.map((p) => (p ? { ...p } : p));

const coronalObliquityDeg = (
  left: { x: number; y: number } | undefined | null,
  right: { x: number; y: number } | undefined | null,
): number => {
  if (!left || !right) return NaN;
  return rad2deg(Math.atan2(right.y - left.y, Math.abs(right.x - left.x)));
};

const fmtPelvisLevel = (d: number, pin?: 'ASIS' | 'PSIS'): string => {
  const base = fmtSignedDeg(d, 'Right', 'Left');
  if (!pin || base === '—') return base;
  return `${base} (${pin})`;
};

// Map landmarks from config
export const IDX = evaluationRules().posture.landmarks;

const plumbX = (view: CaptureSequence, lm: Landmark[], canvasWidth: number) => {
  const A_L = lm?.[IDX.L_ANK], A_R = lm?.[IDX.R_ANK];
  if (view === 'Anterior' || view === 'Posterior') { if (A_L && A_R) return (A_L.x + A_R.x) / 2; return canvasWidth / 2; }
  if (view === 'Left' && A_L) return A_L.x;
  if (view === 'Right' && A_R) return A_R.x;
  return canvasWidth / 2;
};

// Bands from config
const BANDS = evaluationRules().posture.bands;
const DEFAULT_BANDS = {
  headTilt: BANDS.headTilt as [number, number, number],
  pelvicShift: (hipWidthCM: number) => BANDS.pelvicShiftPct.map((p: number) => hipWidthCM * p) as [number, number, number],
  calcTilt: BANDS.calcTilt as [number, number, number],
  cvAngle: BANDS.cvAngle as [number, number, number],
  fwdShift: BANDS.fwdShift as [number, number, number],
  kneeAngle: BANDS.kneeAngle as [number, number, number],
  tibialInclination: BANDS.tibialInclination as [number, number, number],
  pelvicTilt: BANDS.pelvicTilt as [number, number, number],
};

export function analyzeAnterior(originalLm: Landmark[], viewName: CaptureSequence, cmPerPx: number, displayUnit: 'cm' | 'inches' = 'cm', custom?: CustomLandmarks): AnalysisResult[] {
  const asisL = custom?.L_ASIS;
  const asisR = custom?.R_ASIS;
  const lm = cloneLandmarks(originalLm);
  applyFrontalHipIliacBias(lm, 'Anterior');
  applyFrontalShoulderTowardHeadBias(lm);
  const get = (i: number) => lm[i];
  const mid = (a: Landmark, b: Landmark) => (a && b) ? ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } as Landmark) : null;
  const midShoulder = mid(get(IDX.L_SHO), get(IDX.R_SHO)), midAnkles = mid(get(IDX.L_ANK), get(IDX.R_ANK));
  const xPL = plumbX(viewName, lm, 1000);

  const headTiltSigned = (get(IDX.L_EAR) && get(IDX.R_EAR)) ? rad2deg(Math.atan2(get(IDX.R_EAR).y - get(IDX.L_EAR).y, Math.abs(get(IDX.R_EAR).x - get(IDX.L_EAR).x))) : NaN;
  const shoulderObliqSigned = (get(IDX.L_SHO) && get(IDX.R_SHO)) ? rad2deg(Math.atan2(get(IDX.R_SHO).y - get(IDX.L_SHO).y, Math.abs(get(IDX.R_SHO).x - get(IDX.L_SHO).x))) : NaN;
  const hipObliqFromHips = coronalObliquityDeg(get(IDX.L_HIP), get(IDX.R_HIP));
  const pinObliq = (asisL && asisR) ? coronalObliquityDeg(asisL, asisR) : NaN;
  const useAsis = pelvisPinsOverrideHip(pinObliq, hipObliqFromHips);
  const hipObliqSigned = useAsis ? pinObliq : hipObliqFromHips;
  const trunkLeanSigned = (midAnkles && midShoulder) ? rad2deg(Math.atan2(midShoulder.x - xPL, midAnkles.y - midShoulder.y)) : NaN;
  const hipWidthCM = (get(IDX.L_HIP) && get(IDX.R_HIP)) ? distPx(get(IDX.L_HIP), get(IDX.R_HIP)) * cmPerPx : NaN;
  const pxShift = (get(IDX.L_HIP) && get(IDX.R_HIP) && midAnkles) ? (((get(IDX.L_HIP).x + get(IDX.R_HIP).x) / 2) - xPL) : NaN;
  const pelvicShiftSignedCM = isFinite(pxShift) ? pxShift * cmPerPx : NaN;

  return [
    { label: 'Head tilt', raw: headTiltSigned, sev: sev(headTiltSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtSignedDeg(headTiltSigned, 'Right', 'Left'), unit: '°' },
    { label: 'Shoulders level', raw: shoulderObliqSigned, sev: sev(shoulderObliqSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtSignedDeg(shoulderObliqSigned, 'Right', 'Left'), unit: '°' },
    { label: 'Pelvis level', raw: hipObliqSigned, sev: sev(hipObliqSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtPelvisLevel(hipObliqSigned, useAsis ? 'ASIS' : undefined), unit: '°' },
    { label: 'Trunk lean', raw: trunkLeanSigned, sev: sev(trunkLeanSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtSignedDeg(trunkLeanSigned, 'Left', 'Right'), unit: '°' },
    { label: 'Pelvic shift', raw: pelvicShiftSignedCM, sev: sev(pelvicShiftSignedCM, DEFAULT_BANDS.pelvicShift(hipWidthCM)), formattedValue: `${fmtLenWithUnit(Math.abs(pelvicShiftSignedCM), displayUnit)} ${pelvicShiftSignedCM >= 0 ? 'Left' : 'Right'}`, unit: displayUnit },
  ];
}

export function analyzePosterior(originalLm: Landmark[], viewName: CaptureSequence, cmPerPx: number, displayUnit: 'cm' | 'inches' = 'cm', custom?: CustomLandmarks): AnalysisResult[] {
  const lm = cloneLandmarks(originalLm);
  const psisL = custom?.L_PSIS;
  const psisR = custom?.R_PSIS;
  applyFrontalHipIliacBias(lm, 'Posterior');
  applyFrontalShoulderTowardHeadBias(lm);
  const get = (i: number) => lm[i];
  const mid = (a: Landmark, b: Landmark) => (a && b) ? ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } as Landmark) : null;
  const midShoulder = mid(get(IDX.L_SHO), get(IDX.R_SHO)), midAnkles = mid(get(IDX.L_ANK), get(IDX.R_ANK));
  const xPL = plumbX(viewName, lm, 1000);

  const shoulderObliqSigned = (get(IDX.L_SHO) && get(IDX.R_SHO)) ? rad2deg(Math.atan2(get(IDX.R_SHO).y - get(IDX.L_SHO).y, Math.abs(get(IDX.R_SHO).x - get(IDX.L_SHO).x))) : NaN;
  const hipObliqFromHips = coronalObliquityDeg(get(IDX.L_HIP), get(IDX.R_HIP));
  const pinObliq = (psisL && psisR) ? coronalObliquityDeg(psisL, psisR) : NaN;
  const usePsis = pelvisPinsOverrideHip(pinObliq, hipObliqFromHips);
  const hipObliqSigned = usePsis ? pinObliq : hipObliqFromHips;
  const trunkLeanSigned = (midAnkles && midShoulder) ? rad2deg(Math.atan2(midShoulder.x - xPL, midAnkles.y - midShoulder.y)) : NaN;

  const calcTiltL = (get(IDX.L_ANK) && get(IDX.L_HEEL)) ? vertFromLeftCalcaneus(get(IDX.L_ANK), get(IDX.L_HEEL)) : NaN;
  const calcTiltR = (get(IDX.R_ANK) && get(IDX.R_HEEL)) ? vertFrom(get(IDX.R_ANK), get(IDX.R_HEEL)) : NaN;

  return [
    { label: 'Shoulders level', raw: shoulderObliqSigned, sev: sev(shoulderObliqSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtSignedDeg(shoulderObliqSigned, 'Right', 'Left'), unit: '°' },
    { label: 'Pelvis level', raw: hipObliqSigned, sev: sev(hipObliqSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtPelvisLevel(hipObliqSigned, usePsis ? 'PSIS' : undefined), unit: '°' },
    { label: 'Trunk lean', raw: trunkLeanSigned, sev: sev(trunkLeanSigned, DEFAULT_BANDS.headTilt), formattedValue: fmtSignedDeg(trunkLeanSigned, 'Right', 'Left'), unit: '°' },
    { label: 'Calcaneus tilt (L)', raw: calcTiltL, sev: sev(calcTiltL, DEFAULT_BANDS.calcTilt), formattedValue: fmtSignedDeg(calcTiltL, 'Inv', 'Ev'), unit: '°' },
    { label: 'Calcaneus tilt (R)', raw: calcTiltR, sev: sev(calcTiltR, DEFAULT_BANDS.calcTilt), formattedValue: fmtSignedDeg(calcTiltR, 'Inv', 'Ev'), unit: '°' },
  ];
}

export function analyzeLateral(
  lm: Landmark[],
  side: 'Left' | 'Right',
  cmPerPx: number,
  unit: 'cm' | 'inches' = 'cm',
  custom?: CustomLandmarks,
  opts?: { omitPelvicTilt?: boolean }
): AnalysisResult[] {
  const isLeft = side === 'Left';
  // Use only the anatomical chain for the declared capture side. Do not fall back to the
  // opposite side (that mixed Left-view photos with Right-side landmarks).
  const idxLR = (l: number, r: number) => (isLeft ? l : r);
  const E = lm[idxLR(IDX.L_EAR, IDX.R_EAR)] ?? undefined;
  const S = lm[idxLR(IDX.L_SHO, IDX.R_SHO)] ?? undefined;
  const H = lm[idxLR(IDX.L_HIP, IDX.R_HIP)] ?? undefined;
  const K = lm[idxLR(IDX.L_KNEE, IDX.R_KNEE)] ?? undefined;
  const A = lm[idxLR(IDX.L_ANK, IDX.R_ANK)] ?? undefined;

  const hipShift = (H && A) ? ((H.x - A.x) * cmPerPx) : NaN;
  const hipShiftDir = side === 'Left' ? (hipShift <= 0 ? 'Ant' : 'Post') : (hipShift >= 0 ? 'Ant' : 'Post');
  const hipShiftVal = Math.abs(hipShift);

  const shoShift = (S && A) ? ((S.x - A.x) * cmPerPx) : NaN;
  const shoShiftDir = side === 'Left' ? (shoShift <= 0 ? 'Ant' : 'Post') : (shoShift >= 0 ? 'Ant' : 'Post');
  const shoShiftVal = Math.abs(shoShift);

  // Signed offset: positive = shoulders forward of hips (APT pattern),
  // negative = shoulders behind hips (Swayback "C" curve).
  // Convention is normalised across Left/Right views so the gauge always reads Post←0→Ant.
  const torsoOffsetSigned = isFinite(shoShift) && isFinite(hipShift)
    ? side === 'Left'
      ? -(shoShift - hipShift)
      : shoShift - hipShift
    : NaN;

  const kneeRawAngle = (H && K && A) ? angleAt(H, K, A) : NaN;
  let kneeState = 'Neutral';
  let kneeVal = 0;
  if (H && K && A) {
     kneeVal = Math.abs(180 - kneeRawAngle);
     if (Math.abs(A.y - H.y) > 10) {
         const t = (K.y - H.y) / (A.y - H.y);
         const expectedX = H.x + t * (A.x - H.x);
         const diffX = K.x - expectedX; 
         if (side === 'Left') {
             kneeState = diffX < 0 ? 'Flex' : 'Hyperext';
         } else {
             kneeState = diffX > 0 ? 'Flex' : 'Hyperext';
         }
     }
  }

  let kneeHipOffsetSigned = NaN;
  if (H && K) {
      const dx = K.x - H.x;
      kneeHipOffsetSigned = (side === 'Left' ? dx : -dx) * cmPerPx;
  }
  const kneeHipOffsetVal = isFinite(kneeHipOffsetSigned) ? Math.abs(kneeHipOffsetSigned) : NaN;
  const kneeHipOffsetDir = isFinite(kneeHipOffsetSigned) ? (kneeHipOffsetSigned > 0 ? 'Post' : 'Ant') : '';

  let cvAngle = NaN;
  if (E && S) {
      const dx = E.x - S.x;
      const dy = E.y - S.y; 
      const rad = Math.atan2(side === 'Left' ? -dx : dx, -dy);
      cvAngle = rad2deg(rad);
  }

  let shinAngle = NaN;
  if (K && A) {
      const vX = K.x - A.x;
      const vY = K.y - A.y; 
      const rad = Math.atan2(side === 'Left' ? -vX : vX, -vY);
      shinAngle = rad2deg(rad);
  }

  let tilt = NaN;
  let tiltEstimated = false;
  if (!opts?.omitPelvicTilt) {
    if (custom?.ASIS && custom?.PSIS) {
      const MIN_PX = 18; 
      let asis = custom.ASIS as { x: number; y: number };
      let psis = custom.PSIS as { x: number; y: number };
      const expectedAnteriorSign = side === 'Right' ? 1 : -1;
      const rawDx = asis.x - psis.x;
      if (rawDx * expectedAnteriorSign < 0) {
          const tmp = asis; asis = psis; psis = tmp;
      }
      const dy = asis.y - psis.y;
      const dx = asis.x - psis.x;
      if (Math.abs(dx) >= MIN_PX || Math.abs(dy) >= MIN_PX) {
          tilt = rad2deg(Math.atan2(dy, Math.abs(dx)));
      } else {
          tilt = NaN;
      }
    } else if (H && K && A) {
      const thighDX = K.x - H.x;
      const thighDY = K.y - H.y;
      if (Math.abs(thighDY) > 1) {
          let est = rad2deg(Math.atan2(thighDX, Math.abs(thighDY)));
          est = side === 'Left' ? -est : est;
          tilt = est;
          tiltEstimated = true;
      }
    }

    if (!isFinite(tilt) && H && K && A) {
      const thighDX = K.x - H.x;
      const thighDY = K.y - H.y;
      if (Math.abs(thighDY) > 1) {
          let est = rad2deg(Math.atan2(thighDX, Math.abs(thighDY)));
          est = side === 'Left' ? -est : est;
          tilt = est;
          tiltEstimated = true;
      }
    }
  }

  const results: AnalysisResult[] = [
      { label: `Craniovertebral (Head Ant)`, raw: cvAngle, sev: sev(cvAngle, DEFAULT_BANDS.cvAngle), formattedValue: fmtSignedDeg(cvAngle, 'Ant', 'Post'), unit: '°' },
      { label: `Shoulder Anterior Shift`, raw: shoShiftVal, sev: sev(shoShiftVal, DEFAULT_BANDS.fwdShift), formattedValue: `${fmtLenWithUnit(shoShiftVal, unit)} ${shoShiftDir}`, unit },
      { label: `Hip Anterior Shift`, raw: hipShiftVal, sev: sev(hipShiftVal, DEFAULT_BANDS.fwdShift), formattedValue: `${fmtLenWithUnit(hipShiftVal, unit)} ${hipShiftDir}`, unit },
      { label: `Trunk–Hip Offset`, raw: torsoOffsetSigned, sev: sev(torsoOffsetSigned, DEFAULT_BANDS.fwdShift), formattedValue: isFinite(torsoOffsetSigned) ? `${fmtLenWithUnit(Math.abs(torsoOffsetSigned), unit)} ${torsoOffsetSigned >= 0 ? 'Ant' : 'Post'}` : '—', unit },
      { label: `Knee-Hip Offset`, raw: kneeHipOffsetVal, sev: sev(kneeHipOffsetVal, DEFAULT_BANDS.fwdShift), formattedValue: isFinite(kneeHipOffsetVal) ? `${fmtLenWithUnit(kneeHipOffsetVal, unit)} ${kneeHipOffsetDir}` : '—', unit },
      { label: `Knee State`, raw: kneeVal, sev: sev(kneeVal, DEFAULT_BANDS.kneeAngle), formattedValue: `${Math.round(kneeVal)}° ${kneeState}`, unit: '°' },
      { label: `Tibial Inclination`, raw: shinAngle, sev: sev(shinAngle, DEFAULT_BANDS.tibialInclination), formattedValue: fmtSignedDeg(shinAngle, 'Ant', 'Post'), unit: '°' },
  ];

  if (isFinite(tilt)) {
      results.push({ 
          label: `Pelvic Tilt (ASIS-PSIS)`, raw: tilt, sev: sev(tilt, DEFAULT_BANDS.pelvicTilt), 
          formattedValue: `${Math.abs(tilt).toFixed(1)}° ${tilt > 0 ? 'Ant' : 'Post'}${tiltEstimated ? ' (Est.)' : ''}`, unit: '°' 
      });
  }
  return results;
}

export function toAnalysisDataObject(analysisResult: AnalysisResult[]): { [key: string]: string } {
  return analysisResult.reduce((acc, item) => {
      const key = item.label.replace(/[^a-zA-Z0-9]/g, '');
      acc[key] = item.formattedValue;
      return acc;
  }, {} as { [key: string]: string });
}

export { computePostureDiagnoses } from './compute-posture-classification';
export type { ComputePostureDiagnosesOptions } from './compute-posture-classification';
